// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import type { ViewContext } from '../viewContext';
import { SEX_COLOUR } from '../colours';
import { glossaryTerm } from '../glossary';
import { escapeHtml, formatNumber, formatPercent } from '../utils/format';
import { viewHead, wireNameRows } from './shared';

export function renderUnisex(root: HTMLElement, ctx: ViewContext): void {
  const unisex = [...ctx.data.agg.unisex];
  // sort by total popularity by default
  unisex.sort((a, b) => b.total - a.total);

  const rows = unisex
    .map((u) => {
      const tip = escapeHtml(`${u.name}: ${formatNumber(u.boy)} boys, ${formatNumber(u.girl)} girls`);
      return `<div class="rank-row" data-key="${escapeHtml(u.key)}" role="button" tabindex="0" style="grid-template-columns:none;display:grid;grid-template-columns:120px 1fr 108px;gap:var(--space-sm);align-items:center">
        <span class="rank-name">${escapeHtml(u.name)}</span>
        <span style="display:flex;height:16px;border-radius:4px;overflow:hidden;border:1px solid var(--border-subtle)" data-tip="${tip}">
          <span style="width:${(100 - u.girlShare).toFixed(1)}%;background:${SEX_COLOUR.boy}"></span>
          <span style="width:${u.girlShare.toFixed(1)}%;background:${SEX_COLOUR.girl}"></span>
        </span>
        <span class="rank-count" style="width:auto">${formatPercent(u.girlShare, 0)} girls</span>
      </div>`;
    })
    .join('');

  root.innerHTML = `
    ${viewHead(
      'Unisex names',
      `Names that appear in both the boys' and girls' top-100 lists. Some are close to an even ${glossaryTerm(
        'unisex',
        'split',
      )}; others lean heavily one way — and a few have drifted from mostly-boys to mostly-girls (or the reverse) across the decades. Click any name to see its trajectory for each sex.`,
    )}
    <div class="panel">
      <div class="panel-title">${unisex.length} shared names</div>
      <div class="panel-sub">
        Bar split by sex — <span style="color:${SEX_COLOUR.boy};font-weight:600">boys</span> on the left,
        <span style="color:${SEX_COLOUR.girl};font-weight:600">girls</span> on the right — sized by the total babies given the name across all years and both states.
      </div>
      <div class="rank-list">${rows || '<div class="empty">No shared names found.</div>'}</div>
      <div class="legend" style="margin-top:var(--space-md)">
        <span class="legend-item"><span class="legend-swatch" style="background:${SEX_COLOUR.boy}"></span>Boys</span>
        <span class="legend-item"><span class="legend-swatch" style="background:${SEX_COLOUR.girl}"></span>Girls</span>
      </div>
    </div>
  `;

  wireNameRows(root, ctx.openName);
}
